import React, { useState, useEffect, useRef } from 'react';
import { BaseTool, type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import { ApplicationView, useController } from '@aha-app/mvc';
import { type ShapeName, shapeNameToClass } from '../shapes';
import WhiteboardController, {
  BASIC_SHAPES,
} from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import {
  hasIntegrationRecordReference,
  hasRecordReference,
  isShape,
  isShapeIntegrationRecord,
  isShapeRecord,
  isSimpleSelection,
} from '../objects/guards';
import type Shape from '../objects/Shape';
import { escapeTextContent } from '../utils/html';
import {
  NARROW_NOTE_WIDTH,
  NOTE_HEIGHT,
  WIDE_NOTE_WIDTH,
} from '../shapes/ShapeNote';
import { svgToIcon } from '../utils/svg';
import wideStickyNote from '../assets/icons/wideStickyNote.svg';
import WithHint from '../toolbars/WithHint';
import { shapeNameToDescription } from '../objects/Shape';
import { type fabric } from 'fabric';
import { RECORD_DEFAULT_HEIGHT, RECORD_DEFAULT_WIDTH } from '../constants';

// Export only for testing
export function canChangeToRecord(object: fabric.Object): boolean {
  return (
    hasRecordReference(object) &&
    // Ignore shapes that don't have embedded text, except for records
    (['record', 'note'].includes(object.shapeName) ||
      BASIC_SHAPES.includes(object.shapeName))
  );
}

function canChangeToIntegrationRecord(object: fabric.Object): boolean {
  return (
    hasIntegrationRecordReference(object) &&
    (['integrationRecord', 'note'].includes(object.shapeName) ||
      BASIC_SHAPES.includes(object.shapeName))
  );
}

function canChangeToShapeSmartRecord(object: fabric.Object): boolean {
  const shapesThatCanChangeToSmartRecord = [
    ...BASIC_SHAPES,
    'text',
    'note',
    'smartRecord',
  ];
  return (
    isShape(object) &&
    !hasRecordReference(object) &&
    shapesThatCanChangeToSmartRecord.includes(object.shapeName)
  );
}

const getChangeToRecordType = (selection: fabric.Object) => {
  if (selection.getObjects().every(canChangeToRecord)) {
    return 'record';
  }

  if (selection.getObjects().every(canChangeToIntegrationRecord)) {
    return 'integrationRecord';
  }
};

const Popup: React.FC<{
  currentShapeName: ShapeName;
  currentNoteSize: number;
  setCurrentShapeName: (shape: ShapeName) => void;
  setCurrentNoteSize: (width: number) => void;
  selection: fabric.Object;
  hidePopup: () => void;
}> = ApplicationView(
  ({
    currentShapeName,
    currentNoteSize,
    setCurrentShapeName,
    setCurrentNoteSize,
    selection,
    hidePopup,
  }) => {
    const controller = useController(WhiteboardController);

    const changeToRecordType = getChangeToRecordType(selection);
    const canChangeToSmartRecord = selection
      .getObjects()
      .every(obj => canChangeToShapeSmartRecord(obj));

    const convertShapes = (
      shapeName: ShapeName,
      width: number = NARROW_NOTE_WIDTH
    ) => {
      hidePopup();

      setCurrentShapeName(shapeName);

      if (shapeName === 'note') {
        setCurrentNoteSize(width);
      }

      ToolShapeType.action({
        controller,
        selection,
        shapeName,
        width: shapeName === 'note' ? width : undefined,
      });
    };

    return (
      <ToolbarMenu columns={5}>
        {changeToRecordType && (
          <WithHint name='Card'>
            <ToolButton
              active={currentShapeName === changeToRecordType}
              onClick={() => convertShapes(changeToRecordType)}
            >
              {shapeNameToClass(changeToRecordType).renderIcon()}
            </ToolButton>
          </WithHint>
        )}
        {canChangeToSmartRecord && (
          <WithHint name='Aha! shape'>
            <ToolButton
              testId='switch-smartRecord'
              active={currentShapeName === 'smartRecord'}
              onClick={() => convertShapes('smartRecord')}
            >
              {shapeNameToClass('smartRecord').renderIcon()}
            </ToolButton>
          </WithHint>
        )}
        <WithHint name='Text'>
          <ToolButton
            active={currentShapeName === 'text'}
            onClick={() => convertShapes('text')}
          >
            {shapeNameToClass('text').renderIcon()}
          </ToolButton>
        </WithHint>
        <WithHint name='Note'>
          <ToolButton
            active={
              currentShapeName === 'note' &&
              selection
                .getObjects()
                .every(
                  o =>
                    isShape(o) &&
                    o.shapeName === 'note' &&
                    currentNoteSize === NARROW_NOTE_WIDTH
                )
            }
            onClick={() => convertShapes('note', NARROW_NOTE_WIDTH)}
          >
            {shapeNameToClass('note').renderIcon()}
          </ToolButton>
        </WithHint>
        <WithHint name='Wide note'>
          <ToolButton
            active={
              currentShapeName === 'note' &&
              selection
                .getObjects()
                .every(
                  o =>
                    isShape(o) &&
                    o.shapeName === 'note' &&
                    currentNoteSize === WIDE_NOTE_WIDTH
                )
            }
            onClick={() => convertShapes('note', WIDE_NOTE_WIDTH)}
          >
            {svgToIcon(wideStickyNote)}
          </ToolButton>
        </WithHint>
        {controller.switchableShapes.map((shapeName, i) => (
          <WithHint key={i} name={shapeNameToDescription(shapeName)}>
            <ToolButton
              active={shapeName === currentShapeName}
              onClick={() => convertShapes(shapeName)}
            >
              {shapeNameToClass(shapeName).renderIcon()}
            </ToolButton>
          </WithHint>
        ))}
      </ToolbarMenu>
    );
  }
);

const ShapeTypeButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const shapeName = selection.firstObject().shapeName;
    const noteWidth =
      selection.getObjects().find(o => isShape(o) && o.shapeName === 'note')
        ?.width || NARROW_NOTE_WIDTH;
    const [currentShapeName, setCurrentShapeName] = useState(shapeName);
    const [currentNoteSize, setCurrentNoteSize] = useState(noteWidth);
    const tippyRef = useRef<TippyInstance>();
    const ShapeClass = shapeNameToClass(currentShapeName);

    // Update color if the props change.
    useEffect(() => {
      setCurrentShapeName(shapeName);
    }, [shapeName]);

    return (
      <ToolButton
        popup={
          <Popup
            currentShapeName={currentShapeName}
            currentNoteSize={currentNoteSize}
            setCurrentShapeName={setCurrentShapeName}
            setCurrentNoteSize={setCurrentNoteSize}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tippyRef={tippyRef}
        id='shapeChange'
        tool={tool}
      >
        {ShapeClass.renderIcon(shapeName === 'note' ? noteWidth : undefined)}
      </ToolButton>
    );
  }
);

class ToolShapeType extends BaseTool {
  static toolName = 'Shape';

  // Invoke the tool action.
  static action({
    controller,
    selection,
    shapeName,
    width,
  }: {
    controller: WhiteboardController;
    selection: fabric.Object;
    shapeName: ShapeName;
    width?: number;
  }) {
    if (
      shapeName === 'record' &&
      !selection.getObjects().every(canChangeToRecord)
    ) {
      return;
    }

    // Don't use setOnObjects because we want to selectively apply defaults, and also we don't want to update the defaults
    selection.getObjects().forEach((object: Shape) => {
      const props: Partial<Shape> = { shapeName };
      // When converting from a record, apply defaults
      if (isShapeRecord(object) || isShapeIntegrationRecord(object)) {
        Object.assign(props, {
          ...controller.defaultsManager.for('shape', shapeName),
        });

        // If the record doesn't have any text content, try to load it from the record's data
        if (!object.textContent || object.textContent === '<p></p>') {
          const content =
            object.shape.recordInfo?.name ||
            object.integrationRecord?.recordData?.name ||
            '';

          props.textContent = `<p>${escapeTextContent(content)}</p>`;
        }
      }

      /* Remove fill and stroke when converting to text so that if the user then
       converts to a non-text shape they'll get that shapes default appearance */
      if (shapeName === 'text') {
        props.stroke = props.fill = undefined;
      }

      // Exception for note as we might be changing the width
      if (
        object.shapeName !== shapeName ||
        (shapeName === 'note' && width !== object.width)
      ) {
        switch (shapeName) {
          case 'integrationRecord':
          case 'record':
            Object.assign(props, {
              scaleX: 1,
              scaleY: 1,
              width: RECORD_DEFAULT_WIDTH,
              height: RECORD_DEFAULT_HEIGHT,
            });
            break;

          case 'note': {
            if (object.shapeName !== shapeName) {
              // If we are changing the shape to a note, and not just changing
              // the note width, force the scale to be square by taking the
              // average scale
              const newScale = (object.scaleX + object.scaleY) / 2;
              Object.assign(props, {
                scaleX: newScale,
                scaleY: newScale,
              });
            }
            props.width =
              width === WIDE_NOTE_WIDTH ? WIDE_NOTE_WIDTH : NARROW_NOTE_WIDTH;
            props.height = NOTE_HEIGHT;
            break;
          }
        }

        // If we are changing the scale, move the object around
        if (props.scaleX && props.scaleY) {
          const ShapeClass = shapeNameToClass(shapeName);
          const widthDiff =
            object.width * object.scaleX -
            ShapeClass.defaultWidth() * props.scaleX;
          const heightDiff =
            object.height * object.scaleY -
            ShapeClass.defaultHeight() * props.scaleY;

          Object.assign(props, {
            left: object.left + widthDiff / 2,
            top: object.top + heightDiff / 2,
          });
        }
      }

      // If we're no longer a note, then disable endlessStack
      if (shapeName !== 'note') {
        props.endlessStack = false;
      }

      object.set(props);
    });

    // Synchronize each shape
    selection.getObjects().forEach(obj => obj.synchronize());

    if (isSimpleSelection(selection)) {
      selection.syncFromObjects();
    }

    this.toolActionComplete({ controller });
  }

  static buttonComponent() {
    return ShapeTypeButton;
  }
}

export default ToolShapeType;
